/**
 * Zod schemas for Aruba Central API responses.
 *
 * Mirrors the legacy `app_aruba/static/app_aruba/ts/schemas/*` files. The
 * Aruba surface is OAuth-flavored (client id + secret + access/refresh tokens)
 * which is why the key shape is heavier than Meraki's single-string key.
 */
import { z } from "zod";

/* ============================================ */
/* ARUBA KEYS — OAUTH STYLE                     */
/* ============================================ */

/**
 * A registered Aruba Central API key (OAuth client).
 *
 * The secret values (accessToken, refreshToken, clientSecret) come back
 * masked from the server — display-only. `stale` is true when the refresh
 * token has expired and re-auth is needed.
 */
const ArubaKeySchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Masked — e.g. "•••• 8c90". */
  clientId: z.string(),
  /** Optional — only required for some Greenlake tenants. */
  customerId: z.string().nullable().optional(),
  /** Aruba Central regional API gateway hostname. Required for new keys
   *  but nullable for backward compat with rows registered before the
   *  region migration landed. */
  region: z.string().nullable().optional(),
  active: z.boolean(),
  /** True when refresh token has expired. UI surfaces this with a peach pill. */
  stale: z.boolean(),
  createdAt: z.string(),
  /** Epoch seconds when the *access* token expires (Aruba rotates roughly
   *  every 2 hours, but the api-shim auto-refreshes — what matters for
   *  the UI is the refresh-token expiry, currently stamped here too).
   *  Nullable when the token hasn't been minted yet (e.g. freshly imported
   *  bundle that hasn't authenticated once). */
  tokenExpiresAt: z.number().nullish().transform((v) => v ?? undefined),
  /** Total devices reachable through this key. */
  deviceCount: z.number().optional(),
});
export type ArubaKey = z.infer<typeof ArubaKeySchema>;
export const ArubaKeyListSchema = z.array(ArubaKeySchema);

/** Payload shape for create/update. The full secret values are sent
 *  on create — the server returns the masked version on subsequent reads. */
export const ArubaKeyMutationSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, "Name is required"),
  clientId: z.string().min(8, "client_id is required"),
  clientSecret: z.string().min(8, "client_secret is required"),
  accessToken: z.string().min(8, "access_token is required"),
  refreshToken: z.string().min(8, "refresh_token is required"),
  customerId: z.string().optional(),
  /** API gateway hostname (e.g. apigw-prod2.central.arubanetworks.com).
   *  Required by the backend for new registrations — every Central tenant
   *  has exactly one regional gateway and tokens only verify against it. */
  region: z.string().min(8, "API gateway hostname is required"),
  active: z.boolean(),
});
export type ArubaKeyMutation = z.infer<typeof ArubaKeyMutationSchema>;

/* ============================================ */
/* ARUBA DEVICES — INVENTORY                    */
/* ============================================ */

/** A single Aruba device row in the manage-devices table. */
const ArubaDeviceSchema = z.object({
  serial: z.string(),
  name: z.string().nullable(),
  model: z.string().nullable().optional(),
  macAddress: z.string().nullable().optional(),
  group: z.string().nullable(),
  site: z.string().nullable(),
  /** "Up" / "Down" / "Reboot Required" / "Connected" — keep loose. */
  status: z.string().nullable(),
  firmware: z.string().nullable().optional(),
  ipAddress: z.string().nullable().optional(),
  /** "ap" | "switch" | "gateway" | "mc" — populated by the backend
   *  fan-out (see aruba.py list_devices). Loose enum so a future device
   *  type doesn't crash the parser. */
  deviceType: z.string().nullable().optional(),
});
export type ArubaDevice = z.infer<typeof ArubaDeviceSchema>;
export const ArubaDeviceListSchema = z.array(ArubaDeviceSchema);

/* ============================================ */
/* ARUBA SITES                                  */
/* ============================================ */

/**
 * One site row from /api/aruba/sites/. Aruba returns these verbatim — fields
 * are snake_case (vs camelCase elsewhere) because we don't reshape this
 * surface server-side. Loose schema so adding fields server-side doesn't
 * crash the parser. Only the ones the page actually reads are typed.
 */
const ArubaSiteSchema = z.object({
  site_id:                  z.union([z.number(), z.string()]).optional(),
  site_name:                z.string().optional(),
  address:                  z.string().nullable().optional(),
  city:                     z.string().nullable().optional(),
  state:                    z.string().nullable().optional(),
  country:                  z.string().nullable().optional(),
  zipcode:                  z.union([z.string(), z.number()]).nullable().optional(),
  latitude:                 z.union([z.string(), z.number()]).nullable().optional(),
  longitude:                z.union([z.string(), z.number()]).nullable().optional(),
  associated_device_count:  z.number().optional(),
  // Aruba forwards `tags: null` for sites with no tags rather than
  // omitting the key. The schema was `array().optional()` which only
  // accepted array or undefined and rejected null, crashing the parser
  // for every site without tags. `.nullish()` accepts null+undefined,
  // and the transform normalizes both into an empty array so callers
  // can always iterate.
  tags:                     z.array(z.string()).nullish().transform((v) => v ?? []),
}).passthrough(); // allow extra fields the backend forwards verbatim
export type ArubaSite = z.infer<typeof ArubaSiteSchema>;
export const ArubaSiteListSchema = z.array(ArubaSiteSchema);

/* ============================================ */
/* ARUBA WLANS / NETWORKS                       */
/* ============================================ */

/** One row from /api/aruba/wlans/ — surfaced verbatim from Aruba's
 *  /monitoring/v2/networks endpoint. Loose for the same reason as sites. */
const ArubaWlanSchema = z.object({
  essid:    z.string().optional(),
  /** "Employee" / "Guest" / etc — Aruba's network type bucket. */
  type:     z.string().nullable().optional(),
  security: z.string().nullable().optional(),
  /** "WPA2 Personal" / "WPA3 Enterprise" — sometimes split across fields. */
  encryption: z.string().nullable().optional(),
  /** Number of clients currently associated. */
  client_count: z.number().optional(),
  /** Number of APs broadcasting this SSID. */
  ap_count:     z.number().optional(),
}).passthrough();
export type ArubaWlan = z.infer<typeof ArubaWlanSchema>;
export const ArubaWlanListSchema = z.array(ArubaWlanSchema);

/* ============================================ */
/* ARUBA RDV — RECEPTION DEVICE VALIDATION       */
/* ============================================ */

/**
 * Devices in the RDV (Reception Device Validation) queue — devices that have
 * been received at the warehouse and need to be staged into Aruba Central.
 */
const ArubaRdvDeviceSchema = z.object({
  serial: z.string(),
  model: z.string(),
  receivedAt: z.string(),
  receivedBy: z.string(),
  /** "queued" | "validating" | "ok" | "failed". */
  state: z.enum(["queued", "validating", "ok", "failed"]),
  /** Optional human note from the failure case. */
  note: z.string().nullable().optional(),
  /** Target site once validated. */
  targetSite: z.string().nullable().optional(),
});
export type ArubaRdvDevice = z.infer<typeof ArubaRdvDeviceSchema>;
export const ArubaRdvDeviceListSchema = z.array(ArubaRdvDeviceSchema);

/* ============================================ */
/* ARUBA GROUPS                                 */
/* ============================================ */

/**
 * An Aruba Central group — basic unit of config templating. Devices inherit
 * settings from their group.
 */
const ArubaGroupSchema = z.object({
  name: z.string(),
  /** "Wireless" | "Switch" | "Mixed" — keep loose. */
  architecture: z.string().nullable(),
  aosVersion: z.string().nullable(),
  /** Device-type whitelist (e.g. ["AP", "SW"]). */
  allowedDevices: z.array(z.string()).nullable(),
  /** Total devices currently in this group. */
  deviceCount: z.number().optional(),
  /** True when the group is on the GuestTek-blessed baseline. */
  compliant: z.boolean().optional(),
});
export type ArubaGroup = z.infer<typeof ArubaGroupSchema>;
export const ArubaGroupListSchema = z.array(ArubaGroupSchema);

/* ============================================ */
/* ARUBA USERS                                  */
/* ============================================ */

/** An Aruba Central user account that Opus can create or sync. */
const ArubaUserSchema = z.object({
  email: z.string(),
  fullName: z.string(),
  /** "admin" | "viewer" | "operator" — kept loose for forward compat. */
  role: z.string(),
  /** Comma-joined names of the groups they have access to. */
  groupScope: z.string().nullable().optional(),
  /** Last login (ISO) or null if they've never signed in. */
  lastLoginAt: z.string().nullable().optional(),
  active: z.boolean(),
});
export type ArubaUser = z.infer<typeof ArubaUserSchema>;
export const ArubaUserListSchema = z.array(ArubaUserSchema);

/** Payload shape for the User Creation page. */
export const ArubaUserCreateSchema = z.object({
  email: z.string().email("Invalid email"),
  fullName: z.string().min(1, "Required"),
  role: z.enum(["admin", "operator", "viewer"]),
  groupScope: z.string().optional(),
});
export type ArubaUserCreate = z.infer<typeof ArubaUserCreateSchema>;

/* ============================================ */
/* ARUBA ONBOARDING                             */
/* ============================================ */

/** Onboarding step shape — parallels OnboardingStepStatus from Meraki. */
export type ArubaOnboardingStep = {
  slug: string;
  title: string;
  state: "pending" | "running" | "ok" | "error";
  detail?: string;
};

/* ============================================ */
/* ARUBA FIRMWARE UPDATE                        */
/* ============================================ */

/**
 * A firmware version available for an Aruba device family. The Firmware
 * Update page lets the user pick one and push it to a group.
 */
export type ArubaFirmware = {
  version: string;
  /** Device families this version applies to (e.g. ["CX-6300", "AP-655"]). */
  appliesTo: string[];
  releasedAt: string;
  /** True when GuestTek has blessed this version for prod. */
  recommended: boolean;
  /** Major change-log bullets shown in the picker. */
  highlights?: string[];
};
